import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, DollarSign, Coins } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import adminDistributionService from "@/api/services/adminDistributionService";
import adminTransactionsService, {
  type AdminTransactionItem,
} from "@/api/services/adminTransactionsService";
import adminDistributionReportingService, {
  type DistributionRequestsPagination,
  type OrganizationDistributionRequest,
} from "@/api/services/adminDistributionReportingService";
import { getErrorMessage } from '@/utils/errorUtils';

type Period = "1m" | "3m" | "6m" | "1y" | "custom";

type Payment = {
  id: string;
  organisation: string;
  amount: number;
  currency: string;
  method: "cash" | "token";
  status: string;
  createdAt: string;
};

export default function FundDistribution() {
  const { t } = useTranslation();
  const { theme } = useApp();
  const [period, setPeriod] = useState<Period>("1m");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"all" | "cash" | "token">("all");
  const [distributionMode, setDistributionMode] = useState<"thisMonth" | "thisYear" | "custom">("thisMonth");
  const [rangeStart, setRangeStart] = useState<string>("");
  const [rangeEnd, setRangeEnd] = useState<string>("");
  const [actionLoading, setActionLoading] = useState<"cash" | "token" | null>(null);
  const [actionStatus, setActionStatus] = useState<{ type: "success" | "error" | null; message: string }>({
    type: null,
    message: "",
  });

  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<AdminTransactionItem[]>([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsPagination, setTransactionsPagination] = useState<{
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const paymentsListRef = useRef<HTMLDivElement | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<Record<string, unknown> | null>(null);
  const [distributionRequestType, setDistributionRequestType] = useState<"all" | "grant_fund" | "token_distribution">("all");
  const [distributionRequestsLoading, setDistributionRequestsLoading] = useState(false);
  const [distributionRequestsError, setDistributionRequestsError] = useState<string | null>(null);
  const [distributionRequests, setDistributionRequests] = useState<OrganizationDistributionRequest[]>([]);
  const [distributionRequestsPagination, setDistributionRequestsPagination] = useState<DistributionRequestsPagination | null>(null);
  const [distributionRequestsPage, setDistributionRequestsPage] = useState(1);
  const [distributionRequestsLoadingMore, setDistributionRequestsLoadingMore] = useState(false);

  const fetchTransactions = async (pageToLoad: number, append: boolean) => {
    if (pageToLoad === 1) {
      setTransactionsLoading(true);
    } else {
      setLoadingMore(true);
    }
    setTransactionsError(null);
    try {
      const type = activeTab;
      const response = await adminTransactionsService.getTransactions({
        type: type === "all" ? "all" : type,
        page: pageToLoad,
        limit: 10,
      });
      const items = response?.transactions ?? [];
      setTransactions((prev) => (append ? [...prev, ...items] : items));
      setTransactionsPagination(response?.pagination ?? null);
      setTransactionsPage(pageToLoad);
    } catch (err: unknown) {
      setTransactionsError(getErrorMessage(err) || "Failed to load transactions");
    } finally {
      if (pageToLoad === 1) {
        setTransactionsLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const fetchDistributionRequests = async (pageToLoad = 1, append = false) => {
    if (pageToLoad === 1) {
      setDistributionRequestsLoading(true);
    } else {
      setDistributionRequestsLoadingMore(true);
    }
    setDistributionRequestsError(null);
    try {
      const response = await adminDistributionReportingService.getDistributionRequests({
        requestType: distributionRequestType,
        page: pageToLoad,
        limit: 20,
      });
      setDistributionRequests((prev) => (append ? [...prev, ...response.requests] : response.requests));
      setDistributionRequestsPagination(response.pagination);
      setDistributionRequestsPage(pageToLoad);
    } catch (err: unknown) {
      setDistributionRequestsError(getErrorMessage(err) || "Failed to load distribution requests");
      if (!append) {
        setDistributionRequests([]);
        setDistributionRequestsPagination(null);
      }
    } finally {
      if (pageToLoad === 1) {
        setDistributionRequestsLoading(false);
      } else {
        setDistributionRequestsLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    setTransactions([]);
    setTransactionsPage(1);
    setTransactionsPagination(null);
    fetchTransactions(1, false);
    if (paymentsListRef.current) {
      paymentsListRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  useEffect(() => {
    setDistributionRequests([]);
    setDistributionRequestsPagination(null);
    setDistributionRequestsPage(1);
    fetchDistributionRequests(1, false);
  }, [distributionRequestType]);

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        if (period === "custom") {
          if (!startDate || !endDate) {
            setSummaryData(null);
            setSummaryLoading(false);
            return;
          }
          const response = await adminDistributionReportingService.getCustomSummary({
            period: "custom",
            startDate,
            endDate,
          });
          if (!cancelled) setSummaryData((response?.data ?? response ?? null) as unknown as Record<string, unknown> | null);
          return;
        }

        if (period === "1m" || period === "3m" || period === "6m") {
          const periodMap = {
            "1m": "1_month",
            "3m": "3_month",
            "6m": "6_month",
          } as const;
          const response = await adminDistributionReportingService.getSummary(periodMap[period]);
          if (!cancelled) setSummaryData((response?.data ?? response ?? null) as unknown as Record<string, unknown> | null);
          return;
        }

        const end = new Date();
        const start = new Date();
        start.setFullYear(end.getFullYear() - 1);
        const startDateStr = start.toISOString().slice(0, 10);
        const endDateStr = end.toISOString().slice(0, 10);
        const response = await adminDistributionReportingService.getCustomSummary({
          period: "custom",
          startDate: startDateStr,
          endDate: endDateStr,
        });
        if (!cancelled) setSummaryData((response?.data ?? response ?? null) as unknown as Record<string, unknown> | null);
      } catch (err: unknown) {
        if (!cancelled) {
          setSummaryError(getErrorMessage(err) || "Failed to load distribution summary");
          setSummaryData(null);
        }
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };

    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [period, startDate, endDate]);

  const normalizedPayments = useMemo<Payment[]>(() => {
    return transactions.map((payment) => {
      const id =
        payment?.id ||
        payment?._id ||
        payment?.transactionId ||
        payment?.reference ||
        payment?.txRef ||
        "TX";
      const organizationName =
        payment?.organization?.name ||
        payment?.organisation ||
        payment?.organizationName ||
        payment?.orgName;
      const userName = payment?.user?.name || payment?.user?.email;
      const organisation = organizationName || userName || "Organisation";
      const parsedAmount =
        typeof payment?.amount === "number"
          ? payment.amount
          : Number(payment?.amount || 0);
      const amount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
      const currency = payment?.currency || "USD";
      const walletType =
        payment?.walletType || payment?.wallet_type;
      const method: "cash" | "token" =
        walletType === "token" || currency === "KXT" ? "token" : "cash";
      const status =
        payment?.status || payment?.state || "pending";
      const createdAt =
        payment?.createdAt ||
        payment?.created_at ||
        payment?.date ||
        payment?.timestamp ||
        "";
      return {
        id: String(id),
        organisation: String(organisation),
        amount,
        currency: String(currency),
        method,
        status: String(status),
        createdAt: String(createdAt),
      };
    });
  }, [transactions]);

  const filteredPayments = useMemo(() => {
    const now = new Date();
    let fromDate: Date | null = null;
    let toDate: Date | null = null;

    if (period === "custom") {
      fromDate = startDate ? new Date(startDate) : null;
      toDate = endDate ? new Date(endDate) : null;
    } else {
      const days = period === "1m" ? 30 : period === "3m" ? 90 : period === "6m" ? 180 : 365;
      fromDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      toDate = now;
    }

    return normalizedPayments.filter((payment) => {
      if (!payment.createdAt) return true;
      const paymentDate = new Date(payment.createdAt);
      if (Number.isNaN(paymentDate.getTime())) return true;
      if (fromDate && paymentDate < fromDate) return false;
      if (toDate && paymentDate > toDate) return false;
      return true;
    });
  }, [period, startDate, endDate, normalizedPayments]);

  const cashPayments = useMemo(
    () => filteredPayments.filter((payment) => payment.method === "cash"),
    [filteredPayments]
  );

  const tokenPayments = useMemo(
    () => filteredPayments.filter((payment) => payment.method === "token"),
    [filteredPayments]
  );

  const transactionCountLabel = useMemo(() => {
    if (activeTab === "cash") return t('admin.fundDistribution.records.cash', '{{count}} cash records', { count: filteredPayments.length });
    if (activeTab === "token") return t('admin.fundDistribution.records.token', '{{count}} token records', { count: filteredPayments.length });
    return t('admin.fundDistribution.records.total', '{{count}} total records', { count: filteredPayments.length });
  }, [activeTab, filteredPayments.length, t]);

  const hasMoreTransactions = useMemo(() => {
    if (!transactionsPagination) return false;
    const totalPages = transactionsPagination.totalPages;
    if (totalPages !== undefined && totalPages !== null) {
      return transactionsPage < totalPages;
    }
    const total = transactionsPagination.total;
    const limit = transactionsPagination.limit ?? 10;
    if (total !== undefined && total !== null) {
      return transactionsPage * limit < total;
    }
    return false;
  }, [transactionsPagination, transactionsPage]);

  const hasMoreDistributionRequests = useMemo(() => {
    if (!distributionRequestsPagination) return false;
    const totalPages = distributionRequestsPagination.totalPages;
    if (totalPages !== undefined && totalPages !== null) {
      return distributionRequestsPage < totalPages;
    }
    const total = distributionRequestsPagination.total;
    const limit = distributionRequestsPagination.limit ?? 20;
    if (total !== undefined && total !== null) {
      return distributionRequestsPage * limit < total;
    }
    return false;
  }, [distributionRequestsPagination, distributionRequestsPage]);

  const computedTotals = useMemo(() => {
    const totalAmount = filteredPayments.reduce((sum, p) => sum + p.amount, 0);
    const cashAmount = cashPayments.reduce((sum, p) => sum + p.amount, 0);
    const tokenAmount = tokenPayments.reduce((sum, p) => sum + p.amount, 0);
    const pendingCount = filteredPayments.filter(
      (p) => p.status?.toLowerCase() === "pending"
    ).length;
    return { totalAmount, cashAmount, tokenAmount, pendingCount };
  }, [filteredPayments, cashPayments, tokenPayments]);

  const summaryTotals = useMemo(() => {
    const toNumber = (value: unknown) => {
      if (value === undefined || value === null) return undefined;
      const parsed = typeof value === "number" ? value : Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };
    const pickNumber = (...values: unknown[]) => {
      for (const value of values) {
        const parsed = toNumber(value);
        if (parsed !== undefined) return parsed;
      }
      return undefined;
    };
    if (!summaryData) {
      return {
        totalAmount: undefined,
        cashAmount: undefined,
        tokenAmount: undefined,
        pendingCount: undefined,
      };
    }
    const cashAmount = pickNumber(
      summaryData.cashAmount,
      summaryData.cashTotal,
      summaryData.cashVolume,
      (summaryData as unknown as Record<string, unknown>)?.amount,
      (summaryData as unknown as Record<string, unknown>)?.total,
      (summaryData as unknown as Record<string, unknown>)?.totalAmount
    );
    const tokenAmount = pickNumber(
      summaryData.tokenAmount,
      summaryData.tokenTotal,
      summaryData.tokenVolume,
      (summaryData as unknown as Record<string, unknown>)?.amount,
      (summaryData as unknown as Record<string, unknown>)?.total,
      (summaryData as unknown as Record<string, unknown>)?.totalAmount
    );
    const totalAmount =
      pickNumber(
        summaryData.totalAmount,
        summaryData.totalDistributed,
        summaryData.total,
        summaryData.totalVolume,
        summaryData.amount
      ) ?? ((cashAmount ?? 0) + (tokenAmount ?? 0));
    const pendingCount = pickNumber(
      summaryData.pendingCount,
      summaryData.pending,
      summaryData.pendingItems,
      summaryData.pendingTransactions,
      (summaryData as unknown as Record<string, unknown>)?.count,
      (summaryData as unknown as Record<string, unknown>)?.total
    );

    return {
      totalAmount,
      cashAmount,
      tokenAmount,
      pendingCount,
    };
  }, [summaryData]);

  const displayTotals = useMemo(() => {
    return {
      totalAmount: summaryTotals.totalAmount ?? computedTotals.totalAmount,
      cashAmount: summaryTotals.cashAmount ?? computedTotals.cashAmount,
      tokenAmount: summaryTotals.tokenAmount ?? computedTotals.tokenAmount,
      pendingCount: summaryTotals.pendingCount ?? computedTotals.pendingCount,
    };
  }, [summaryTotals, computedTotals]);

  const statCards = useMemo(() => {
    const totalAmountLabel = summaryLoading
      ? "..."
      : `${displayTotals.totalAmount.toLocaleString()} USD`;
    const cashAmountLabel = summaryLoading
      ? "..."
      : `${displayTotals.cashAmount.toLocaleString()} USD`;
    const tokenAmountLabel = summaryLoading
      ? "..."
      : `${displayTotals.tokenAmount.toLocaleString()} USD`;
    const pendingLabel = summaryLoading
      ? "..."
      : `${displayTotals.pendingCount}`;
    return [
      { label: t('admin.fundDistribution.metrics.totalDistributed', 'Total Distributed'), value: totalAmountLabel, accent: "text-blue-600" },
      { label: t('admin.fundDistribution.metrics.cashVolume', 'Cash Volume'), value: cashAmountLabel, accent: "text-emerald-600" },
      { label: t('admin.fundDistribution.metrics.tokenVolume', 'Token Volume'), value: tokenAmountLabel, accent: "text-indigo-600" },
      { label: t('admin.fundDistribution.metrics.pendingItems', 'Pending Items'), value: pendingLabel, accent: "text-amber-600" },
    ];
  }, [displayTotals, summaryLoading, t]);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = `${currentYear}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const isDecember = now.getMonth() === 11;

  const applyPresetRange = (mode: "thisMonth" | "thisYear") => {
    if (mode === "thisMonth") {
      setRangeStart(currentMonth);
      setRangeEnd(currentMonth);
      return;
    }
    setRangeStart(`${currentYear}-01`);
    setRangeEnd(`${currentYear}-12`);
  };

  const setMode = (mode: "thisMonth" | "thisYear" | "custom") => {
    setDistributionMode(mode);
    setActionStatus({ type: null, message: "" });
    if (mode === "thisMonth") applyPresetRange("thisMonth");
    if (mode === "thisYear") applyPresetRange("thisYear");
    if (mode === "custom") {
      if (!rangeStart) setRangeStart(currentMonth);
      if (!rangeEnd) setRangeEnd(currentMonth);
    }
  };

  const handleDistribution = async (mode: "cash" | "token") => {
    setActionLoading(mode);
    setActionStatus({ type: null, message: "" });
    try {
      const start = rangeStart || currentMonth;
      const end = rangeEnd || currentMonth;
      if (distributionMode === "custom" && (!start || !end)) {
        setActionStatus({ type: "error", message: "Please select a valid start and end month." });
        setActionLoading(null);
        return;
      }
      if (start > end) {
        setActionStatus({ type: "error", message: "Start month cannot be after end month." });
        setActionLoading(null);
        return;
      }
      const payload = {
        period: "range" as const,
        startDate: start,
        endDate: end,
      };
      const response =
        mode === "token"
          ? await adminDistributionService.tokenDistribution(payload)
          : await adminDistributionService.grantDistribution(payload);
      setActionStatus({
        type: "success",
        message: response?.message || "Distribution request submitted successfully.",
      });
    } catch (error: unknown) {
      setActionStatus({
        type: "error",
        message:
          (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
          (error as { message?: string })?.message ||
          "Failed to submit distribution request.",
      });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div
        className={`relative overflow-hidden rounded-3xl border p-6 sm:p-8 ${
          theme === "dark"
            ? "bg-gradient-to-br from-[#1c1d20] via-[#191a1d] to-[#141517] border-gray-800"
            : "bg-gradient-to-br from-white via-[#f8fafc] to-[#eef2ff] border-gray-200"
        }`}
      >
        <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute -left-10 bottom-0 h-32 w-32 rounded-full bg-emerald-400/10 blur-3xl" />

        <div className="relative z-10 flex flex-col gap-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <p className={`text-xs uppercase tracking-[0.2em] ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                {t('admin.fundDistribution.adminConsole', 'Admin Console')}
              </p>
              <h2 className={`text-2xl sm:text-3xl font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                {t('admin.funddistribution.title', 'Fund Distribution')}
              </h2>
              <p className={`text-sm mt-2 ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                {t('admin.fundDistribution.subtitle', 'Review payments and track distribution requests at a glance.')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {([
                { key: "1m", label: t('admin.common.periods.1m', "1 Month") },
                { key: "3m", label: t('admin.common.periods.3m', "3 Months") },
                { key: "6m", label: t('admin.common.periods.6m', "6 Months") },
                { key: "1y", label: t('admin.common.periods.1y', "1 Year") },
                { key: "custom", label: t('admin.common.periods.custom', "Custom") },
              ] as { key: Period; label: string }[]).map((item) => {
                const active = period === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => setPeriod(item.key)}
                    className={`px-3 py-2 rounded-full text-xs font-semibold transition ${
                      active
                        ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                        : theme === "dark"
                        ? "bg-[#2a2b2d] text-gray-300 hover:bg-[#333436]"
                        : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {period === "custom" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className={`text-xs font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
                Start Date
                <div
                  className={`mt-2 flex items-center gap-2 rounded-xl border px-3 py-2 ${
                    theme === "dark" ? "bg-[#2a2b2d] border-gray-700" : "bg-white border-gray-200"
                  }`}
                >
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full bg-transparent text-sm outline-none text-gray-700 dark:text-gray-200"
                  />
                </div>
              </label>
              <label className={`text-xs font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
                End Date
                <div
                  className={`mt-2 flex items-center gap-2 rounded-xl border px-3 py-2 ${
                    theme === "dark" ? "bg-[#2a2b2d] border-gray-700" : "bg-white border-gray-200"
                  }`}
                >
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full bg-transparent text-sm outline-none text-gray-700 dark:text-gray-200"
                  />
                </div>
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {statCards.map((stat) => (
              <div
                key={stat.label}
                className={`rounded-2xl border p-4 ${
                  theme === "dark" ? "bg-[#1f2022] border-gray-800" : "bg-white border-gray-200"
                }`}
              >
                <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>{stat.label}</p>
                <p className={`text-lg font-semibold mt-2 ${stat.accent}`}>{stat.value}</p>
              </div>
            ))}
          </div>
          {summaryError && (
            <p className={`text-xs ${theme === "dark" ? "text-rose-300" : "text-rose-600"}`}>
              {summaryError}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className={`flex-1 rounded-2xl border p-5 ${theme === "dark" ? "bg-[#1f2022] border-gray-800" : "bg-white border-gray-200"}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h3 className={`text-lg font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                {t('admin.funddistribution.payments.title', 'Payments')}
              </h3>
              <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                {transactionCountLabel}
              </p>
            </div>
            <div className={`flex items-center gap-2 rounded-full p-1 ${theme === "dark" ? "bg-[#2a2b2d]" : "bg-gray-100"}`}>
              {([
                { key: "all", label: t('admin.common.all', 'All') },
                { key: "cash", label: t('admin.fundDistribution.method.cash', 'Cash') },
                { key: "token", label: t('admin.fundDistribution.method.token', 'Token') },
              ] as { key: "all" | "cash" | "token"; label: string }[]).map((tab) => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-3 py-2 rounded-full text-xs font-semibold transition ${
                      active
                        ? "bg-white text-gray-900 shadow-sm"
                        : theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            ref={paymentsListRef}
            className="space-y-3 max-h-[520px] overflow-y-auto pr-1"
          >
            {transactionsLoading && (
              <div className="text-sm text-gray-500 text-center py-6">
                Loading transactions...
              </div>
            )}
            {transactionsError && !transactionsLoading && (
              <div className="text-sm text-red-500 text-center py-6">
                {transactionsError}
              </div>
            )}
            {!transactionsLoading && !transactionsError && filteredPayments.map((payment) => {
              const id = payment.id;
              const org = payment.organisation;
              const method = payment.method;
              const status = payment.status;
              const amount = payment.amount;
              const currency = payment.currency;
              const createdAt = payment.createdAt;
              const dateLabel = createdAt ? new Date(createdAt).toLocaleString() : "-";
              return (
                <div
                  key={id}
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border px-4 py-3 ${
                    theme === "dark" ? "border-gray-800 bg-[#1a1b1e]" : "border-gray-200 bg-white"
                  } shadow-sm`}
                >
                  <div>
                    <p className={`text-sm font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                      {org}
                    </p>
                    <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                      {id} - {dateLabel}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        method === "cash"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {method === "cash" ? t('admin.fundDistribution.method.cash', 'Cash') : t('admin.fundDistribution.method.token', 'Token')}
                    </span>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        status?.toLowerCase() === "completed"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {t(`admin.fundDistribution.status.${status?.toLowerCase()}`, status)}
                    </span>
                    <span className={`text-sm font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                      {amount.toLocaleString()} {currency}
                    </span>
                  </div>
                </div>
              );
            })}
            {!transactionsLoading && !transactionsError && filteredPayments.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-8">
                No transactions found for this selection.
              </div>
            )}
          </div>
          {!transactionsLoading && !transactionsError && hasMoreTransactions && (
            <div className="flex justify-center pt-3 pb-1">
              <button
                onClick={() => fetchTransactions(transactionsPage + 1, true)}
                disabled={loadingMore}
                className="inline-flex items-center justify-center rounded-full border border-gray-200 px-5 py-2 text-xs font-semibold text-gray-700 transition hover:border-gray-300 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:border-gray-600"
              >
                {loadingMore ? t('admin.common.loading', 'Loading...') : t('admin.common.loadMore', 'Load More')}
              </button>
            </div>
          )}
        </div>

        <div className={`flex-1 rounded-2xl border p-5 ${theme === "dark" ? "bg-[#1f2022] border-gray-800" : "bg-white border-gray-200"}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`text-lg font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                {t('admin.funddistribution.requests.title', 'Distribution Requests')}
              </h3>
              <p className={`text-xs mt-1 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                {t('admin.fundDistribution.requests.description', 'Review organization distribution requests and owner details.')}
              </p>
            </div>
            <span className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
              {distributionRequestsPagination?.total ?? distributionRequests.length} {t('admin.fundDistribution.requests.count', 'requests')}
            </span>
          </div>

          <div className="mb-4 flex items-center gap-2 rounded-full border border-gray-200 p-1 dark:border-gray-700 w-fit">
            <button
              onClick={() => setDistributionRequestType("all")}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition ${
                distributionRequestType === "all"
                  ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                  : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
              }`}
            >{t('admin.common.all', 'All')}</button>
            <button
              onClick={() => setDistributionRequestType("grant_fund")}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition ${
                distributionRequestType === "grant_fund"
                  ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                  : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
              }`}
            >{t('admin.fundDistribution.requests.filter.grant', 'Grant')}</button>
            <button
              onClick={() => setDistributionRequestType("token_distribution")}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition ${
                distributionRequestType === "token_distribution"
                  ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                  : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
              }`}
            >{t('admin.fundDistribution.requests.filter.token', 'Token')}</button>
          </div>

          <div className="space-y-3">
            {distributionRequestsLoading && (
              <div className="text-sm text-gray-500 text-center py-8">
                Loading distribution requests...
              </div>
            )}
            {!distributionRequestsLoading && distributionRequestsError && (
              <div className="text-sm text-red-500 text-center py-8">
                {distributionRequestsError}
              </div>
            )}
            {!distributionRequestsLoading && !distributionRequestsError && distributionRequests.map((request, index) => {
              const requestId = request.id || request._id || `request-${index}`;
              const ownerName = [request.owner?.firstName, request.owner?.lastName].filter(Boolean).join(" ").trim() || "N/A";
              const ownerEmail = request.owner?.email || "N/A";
              const requestType = request.requestType || "N/A";
              const reason = request.reason || "No reason provided";
              const status = request.status || "pending";
              const createdAt = request.createdAt ? new Date(request.createdAt).toLocaleString() : "N/A";

              return (
                <div
                  key={requestId}
                  className={`rounded-2xl border px-4 py-4 ${
                    theme === "dark" ? "border-gray-800 bg-[#1a1b1e]" : "border-gray-200 bg-white"
                  } shadow-sm`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="space-y-1">
                      <p className={`text-sm font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                        {request.organizationName || "Organization"}
                      </p>
                      <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                        Owner ID: {request.ownerId || "N/A"}
                      </p>
                      <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                        {ownerName} - {ownerEmail}
                      </p>
                      <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                        Requested: {createdAt}
                      </p>
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">
                          {requestType}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            status.toLowerCase() === "approved" || status.toLowerCase() === "completed"
                              ? "bg-emerald-100 text-emerald-700"
                              : status.toLowerCase() === "rejected"
                                ? "bg-red-100 text-red-700"
                                : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {t(`admin.fundDistribution.status.${status?.toLowerCase()}`, status)}
                        </span>
                      </div>
                      <p className={`text-xs max-w-sm text-left md:text-right ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
                        {reason}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            {!distributionRequestsLoading && !distributionRequestsError && distributionRequests.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-8">{t('admin.fundDistribution.requests.empty', 'No distribution requests yet.')}</div>
            )}
            {!distributionRequestsLoading && !distributionRequestsError && hasMoreDistributionRequests && (
              <div className="flex justify-center pt-3 pb-1">
                <button
                  onClick={() => fetchDistributionRequests(distributionRequestsPage + 1, true)}
                  disabled={distributionRequestsLoadingMore}
                  className="inline-flex items-center justify-center rounded-full border border-gray-200 px-5 py-2 text-xs font-semibold text-gray-700 transition hover:border-gray-300 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:border-gray-600"
                >
                  {distributionRequestsLoadingMore ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`rounded-2xl border p-5 ${theme === "dark" ? "bg-[#1f2022] border-gray-800" : "bg-white border-gray-200"}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className={`text-lg font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>{t('admin.fundDistribution.actions.title', 'Distribution Actions')}</h3>
            <p className={`text-xs mt-1 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>{t('admin.fundDistribution.actions.subtitle', 'Choose a period and trigger payout distributions.')}</p>
          </div>
          <div className="flex flex-col items-start gap-2">
            <div className={`flex items-center gap-2 rounded-full p-1 border border-gray-200 dark:border-gray-700`}>
              <button
                onClick={() => setMode("thisMonth")}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition ${
                  distributionMode === "thisMonth"
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                    : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t('admin.fundDistribution.actions.thisMonth', 'This Month')} ({currentMonth})
              </button>
              <button
                onClick={() => setMode("thisYear")}
                disabled={!isDecember}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition ${
                  distributionMode === "thisYear"
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                    : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {t('admin.fundDistribution.actions.thisYear', 'This Year')} ({currentYear})
              </button>
              <button
                onClick={() => setMode("custom")}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition ${
                  distributionMode === "custom"
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                    : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >{t('admin.fundDistribution.actions.custom', 'Custom')}</button>
            </div>
            {!isDecember && (
              <p className={`text-[11px] ${theme === "dark" ? "text-gray-500" : "text-gray-500"}`}>{t('admin.fundDistribution.actions.unlocksDec', 'This year distribution unlocks in December.')}</p>
            )}
          </div>
        </div>
        {distributionMode === "custom" && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className={`text-xs font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
              Start Month
              <div
                className={`mt-2 flex items-center gap-2 rounded-xl border px-3 py-2 ${
                  theme === "dark" ? "bg-[#2a2b2d] border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <Calendar className="w-4 h-4 text-gray-400" />
                <input
                  type="month"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="w-full bg-transparent text-sm outline-none text-gray-700 dark:text-gray-200"
                />
              </div>
            </label>
            <label className={`text-xs font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
              End Month
              <div
                className={`mt-2 flex items-center gap-2 rounded-xl border px-3 py-2 ${
                  theme === "dark" ? "bg-[#2a2b2d] border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <Calendar className="w-4 h-4 text-gray-400" />
                <input
                  type="month"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="w-full bg-transparent text-sm outline-none text-gray-700 dark:text-gray-200"
                />
              </div>
            </label>
          </div>
        )}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={() => handleDistribution("cash")}
            disabled={actionLoading !== null}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-black text-white font-semibold hover:bg-gray-900 transition disabled:opacity-60"
          >
            <DollarSign className="w-4 h-4" />
            {actionLoading === "cash" ? t('admin.fundDistribution.actions.distributingCash', 'Distributing Cash...') : t('admin.fundDistribution.actions.distributeCash', 'Distribute Cash to Organisation')}
          </button>
          <button
            onClick={() => handleDistribution("token")}
            disabled={actionLoading !== null}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition disabled:opacity-60"
          >
            <Coins className="w-4 h-4" />
            {actionLoading === "token" ? t('admin.fundDistribution.actions.distributingToken', 'Distributing Token...') : t('admin.fundDistribution.actions.distributeToken', 'Distribute Token')}
          </button>
        </div>
        {actionStatus.type && (
          <div
            className={`mt-4 text-xs font-medium ${
              actionStatus.type === "success" ? "text-emerald-600" : "text-red-500"
            }`}
          >
            {actionStatus.message}
          </div>
        )}
      </div>
    </div>
  );
}
